'use client';
import * as React from 'react';
import { createEventBus } from '@/shared/utils';
import { Changepassword } from '@/shared/i18n';
import { ChangePasswordScreen, ChangePasswordEvents, ChangePasswordEventKeys } from '@/profile';
import { ProfileLoader } from 'src/modules/profile/ui/shared/module-loader';
import { useFirebaseAuth } from '@/shared/hooks';
import { useRouter } from 'next/navigation';
import { useAuthStore, useServerAuth } from '@/auth';

interface ChangePasswordContainerProps {
  dict: Changepassword;
}

export function ChangePasswordContainer({ dict }: ChangePasswordContainerProps) {
  const changePasswordEventBus = createEventBus<ChangePasswordEvents>('change-password-scope');
  const router = useRouter();
  const { user } = useAuthStore();
  const { logout, hydrated } = useServerAuth();

  const [isLoading, setIsLoading] = React.useState(true);
  const auth = useFirebaseAuth();

  const handleRedirect = React.useCallback(
    (url: string) => {
      if (url === '/auth/login' && !user?.email) {
        router.push(url);
        return;
      }
      if (url === '/auth/login' && logout) logout().then(() => router.push(url));
    },
    [router, logout, user?.email],
  );

  React.useEffect(() => {
    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    if (hydrated && !user?.email) {
      handleRedirect('/auth/login');
    }

    changePasswordEventBus.on(ChangePasswordEventKeys.redirect.to, handleRedirect);
    return () => {
      changePasswordEventBus.off(ChangePasswordEventKeys.redirect.to, handleRedirect);
    };
  }, [user?.email, hydrated, changePasswordEventBus]);

  if (isLoading || !auth) return <ProfileLoader key="profile-loader" height={221} />;

  return (
    <ChangePasswordScreen
      auth={auth}
      eventBus={changePasswordEventBus}
      dict={dict}
      key="change-password-screen"
    />
  );
}

export default ChangePasswordContainer;
